/**
 * Client API Matériauthèque IA — wraps les endpoints backend.
 * Toutes les routes nécessitent l'auth (cookie session).
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/materialtheque`;
const opts = { withCredentials: true };

export const matApi = {
  /**
   * Upload PDF + démarre extraction async.
   * @returns {Promise<Job>}
   */
  uploadAndExtract: async (file, projectId = null, onUploadProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    if (projectId) fd.append('project_id', projectId);
    const r = await axios.post(`${API}/jobs/upload-and-extract`, fd, {
      ...opts,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
    return r.data;
  },

  /** Statut + progress du job (polling). */
  getJob: async (jobId) => {
    const r = await axios.get(`${API}/jobs/${jobId}`, opts);
    return r.data;
  },

  /** Résultat complet (sections + items). */
  getJobResult: async (jobId) => {
    const r = await axios.get(`${API}/jobs/${jobId}/result`, opts);
    return r.data;
  },

  listJobs: async () => {
    const r = await axios.get(`${API}/jobs`, opts);
    return r.data;
  },

  /** Récupère le dernier job lié à un projet (tous statuts). */
  getProjectLatestJob: async (projectId) => {
    const r = await axios.get(`${API}/projects/${projectId}/latest-job`, opts);
    return r.data; // { job: {...} | null }
  },

  /** Liste TOUS les jobs d'un projet (historique). */
  listProjectJobs: async (projectId) => {
    const r = await axios.get(`${API}/projects/${projectId}/jobs`, opts);
    return r.data; // { jobs: [...], total: N }
  },

  updateMaterial: async (materialId, patch) => {
    const r = await axios.put(`${API}/materials/${materialId}`, patch, opts);
    return r.data;
  },

  /** V15.11 — arbitrage « Retenu » par rôle. */
  retainMaterial: async (materialId) => {
    const r = await axios.post(`${API}/materials/${materialId}/retain`, {}, opts);
    return r.data;
  },
  getBriefReport: async (projectId) => {
    const r = await axios.get(`${API}/projects/${projectId}/brief-report`, opts);
    return r.data;
  },

  resetMaterialToAi: async (materialId) => {
    const r = await axios.post(`${API}/materials/${materialId}/reset-ai`, {}, opts);
    return r.data;
  },

  deleteMaterial: async (materialId) => {
    const r = await axios.delete(`${API}/materials/${materialId}`, opts);
    return r.data;
  },

  approveJob: async (jobId, projectId = null) => {
    const r = await axios.post(`${API}/jobs/${jobId}/approve`, { project_id: projectId }, opts);
    return r.data;
  },

  /** Sauvegarde l'extraction d'un job comme un Style réutilisable. */
  saveJobAsStyle: async (jobId, { name, description = '' }) => {
    const r = await axios.post(`${API}/jobs/${jobId}/save-as-style`, { name, description }, opts);
    return r.data; // { ok, style: {...} }
  },

  deleteJob: async (jobId) => {
    const r = await axios.delete(`${API}/jobs/${jobId}`, opts);
    return r.data;
  },

  // ---- Sans CCTP : photos 3D / vierge / enrichissement ----
  uploadPhotos: async (projectId, files, onUploadProgress) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    const r = await axios.post(`${API}/projects/${projectId}/photos/upload`, fd, {
      ...opts, headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress,
    });
    return r.data; // { files: [...] }
  },

  listPhotoCandidates: async (projectId) => {
    const r = await axios.get(`${API}/projects/${projectId}/photos/candidates`, opts);
    return r.data; // { files, room_types }
  },

  classifyPhotos: async (projectId, fileIds) => {
    const r = await axios.post(`${API}/projects/${projectId}/photos/classify`, { file_ids: fileIds }, { ...opts, timeout: 180000 });
    return r.data; // { results: [{file_id, room_type, ...}] }
  },

  extractFromPhotos: async (projectId, photos) => {
    const r = await axios.post(`${API}/jobs/extract-from-photos`, { project_id: projectId, photos }, opts);
    return r.data; // job
  },

  createBlankJob: async (projectId) => {
    const r = await axios.post(`${API}/projects/${projectId}/jobs/blank`, {}, opts);
    return r.data; // job
  },

  addManualMaterial: async (jobId, body) => {
    const r = await axios.post(`${API}/jobs/${jobId}/materials`, body, opts);
    return r.data; // material doc
  },

  enrichFromCctp: async (jobId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await axios.post(`${API}/jobs/${jobId}/enrich-from-cctp`, fd, {
      ...opts, headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data; // job
  },

  /** « Générer la pièce » — rendu Nano Banana d'une section (≈20-40 s). */
  renderRoom: async (jobId, sectionId, { keep_source_view = false, instruction = null } = {}) => {
    const r = await axios.post(`${API}/jobs/${jobId}/sections/${sectionId}/render`, { keep_source_view, instruction }, { ...opts, timeout: 180000 });
    return r.data; // { ok, render }
  },

  detectDuplicates: async (jobId) => {
    const r = await axios.post(`${API}/jobs/${jobId}/duplicates/detect`, {}, { ...opts, timeout: 90000 });
    return r.data; // { groups, count }
  },
  mergeDuplicates: async (jobId, keepId, mergeIds) => {
    const r = await axios.post(`${API}/jobs/${jobId}/duplicates/merge`, { keep_id: keepId, merge_ids: mergeIds }, opts);
    return r.data;
  },
  undoMerge: async (jobId, keepId) => {
    const r = await axios.post(`${API}/jobs/${jobId}/duplicates/undo`, { keep_id: keepId }, opts);
    return r.data;
  },
};

/** Résout une URL de vignette relative (/api/...) vers le backend. */
export const resolveThumbUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};

export const fileDownloadUrl = (fileId) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}/download`;

export default matApi;
